/**
 * Dialog asking the operator how to import a `.nx` bundle.
 *
 * v3 bundles carry a manifest + artifact list, which we surface up front so
 * the operator sees exactly what they're about to import. The import mode
 * (clone / override) lives under Advanced because the safe default
 * (override for v3, clone for legacy) is right for the common case. The
 * other knobs (activate / missing-dependency policy) are always set to
 * safe defaults — activate inherits the bundle's hint, and missing
 * references are scrubbed with a warning so the import doesn't fail on
 * stale tool refs.
 *
 * Legacy v1/v2 bundles ship as a bare list with no manifest — they're
 * locked to clone (no stable IDs to honor) and have no options to
 * configure, so the dialog reduces to title + filename + import button.
 */

import { Fragment, useMemo, useState } from "react";
import BaseDialog from "../Modals/BaseDialog";
import { Label } from "../shadcn/label";
import { RadioGroup, RadioGroupItem } from "../shadcn/radio-group";
import { cn } from "@/lib/utils";
import { ChevronRight, ExternalLink } from "lucide-react";
import { NoxusIcon } from "../UI/NoxusIcon";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../shadcn/collapsible";

export type ImportMode = "clone" | "version" | "replace";
export type MissingDependencyPolicy = "fail" | "warn";

export interface ImportOptions {
  mode: ImportMode;
  activate: boolean;
  on_missing_dependency: MissingDependencyPolicy;
}

interface MetadataEntry {
  label: string;
  value: string;
}

interface BundleArtifactPreview {
  type: string;
  name: string;
  id: string;
  icon?: string;
}

interface DecodedBundle {
  // generic, key-agnostic provenance to render — whatever the manifest carries
  metadata: MetadataEntry[];
  setActiveOnImport: boolean;
  artifacts: BundleArtifactPreview[];
}

// v4 kind -> the v3-style type the preview UI keys on (TYPE_LABELS).
const V4_KIND_TO_TYPE: Record<string, string> = {
  flow: "workflow",
  agent: "assistant",
  knowledge_base: "knowledge_base",
  inbox: "inbox",
  file: "file",
};

// manifest keys that are functional, not display provenance
const META_SKIP = new Set([
  "apiVersion",
  "kind",
  "setActiveOnImport",
  "set_active_on_import",
  "format_version",
]);

function humanizeKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase
    .replace(/[_-]+/g, " ") // snake / kebab
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function formatMetaValue(key: string, value: string): string {
  if (/(at|date|time)$/i.test(key)) {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  return value;
}

function toMetadata(raw: Record<string, unknown>): MetadataEntry[] {
  return Object.entries(raw)
    .filter(
      ([k, v]) => !META_SKIP.has(k) && v != null && v !== "" && v !== "null"
    )
    .map(([k, v]) => ({
      label: humanizeKey(k),
      value: formatMetaValue(k, String(v)),
    }));
}

/** Best-effort preview parse of a v4 `.nx`; the backend does the authoritative parse. */
function decodeV4Bundle(text: string): DecodedBundle | null {
  if (!text.includes("apiVersion: noxus/v4")) return null;
  const docs = text.split(/^---\s*$/m);
  const metaRaw: Record<string, string> = {};
  let setActiveOnImport = false;
  const artifacts: BundleArtifactPreview[] = [];
  const unquote = (v: string) => v.replace(/^(['"])([\s\S]*)\1$/, "$2");
  const field = (doc: string, key: string): string | undefined => {
    const m = new RegExp(`^\\s*${key}:\\s*(.+?)\\s*$`, "m").exec(doc);
    return m ? unquote(m[1].trim()) : undefined;
  };
  for (const doc of docs) {
    const kind = field(doc, "kind");
    if (!kind) continue;
    if (kind === "Bundle") {
      // scan every indented key:value (the metadata + exportOptions children)
      const re = /^\s+(\w+):\s*(.+?)\s*$/gm;
      let m: RegExpExecArray | null;
      while ((m = re.exec(doc)) !== null) metaRaw[m[1]] = unquote(m[2]);
      setActiveOnImport = metaRaw.setActiveOnImport === "true";
      continue;
    }
    const name = field(doc, "name");
    const id = field(doc, "id");
    artifacts.push({
      type: V4_KIND_TO_TYPE[kind] ?? kind,
      name: name || id || "Untitled",
      id: id || "",
    });
  }
  return artifacts.length
    ? { metadata: toMetadata(metaRaw), setActiveOnImport, artifacts }
    : null;
}

/** Handles v4 plaintext YAML and legacy base64; null for v1/v2 bundles with no manifest. */
export function decodeBundle(fileContent: string): DecodedBundle | null {
  const v4 = decodeV4Bundle(fileContent);
  if (v4) return v4;
  try {
    let raw = fileContent.trim();
    // Old exports went through JSON.stringify, so the file is a JSON-quoted
    // base64 string. Strip the wrapping if present.
    if (raw.startsWith('"') && raw.endsWith('"')) {
      try {
        raw = JSON.parse(raw);
      } catch {
        raw = raw.slice(1, -1);
      }
    }
    const decoded = atob(raw);
    const json = JSON.parse(decoded);
    if (json?.format_version !== "v3") return null;
    const rawArtifacts = Array.isArray(json?.artifacts) ? json.artifacts : [];
    const artifacts: BundleArtifactPreview[] = rawArtifacts.map(
      (a: Record<string, unknown>) => ({
        type: String(a.type ?? "unknown"),
        name: String(a.name ?? a.id ?? "Untitled"),
        id: String(a.id ?? a.hidden_id ?? ""),
        icon:
          (a.meta as Record<string, unknown> | undefined)?.icon !== undefined
            ? String((a.meta as Record<string, unknown>).icon)
            : undefined,
      })
    );
    return {
      metadata: toMetadata((json?.manifest as Record<string, unknown>) ?? {}),
      setActiveOnImport: !!json?.export_options?.set_active_on_import,
      artifacts,
    };
  } catch {
    return null;
  }
}

const TYPE_LABELS: Record<
  string,
  { label: string; icon: string; href?: (id: string) => string }
> = {
  workflow: { label: "Flow", icon: "workflow", href: (id) => `/flows/${id}` },
  assistant: {
    label: "Agent",
    icon: "coworker",
    href: (id) => `/assistant/${id}`,
  },
  knowledge_base: {
    label: "Knowledge base",
    icon: "kb",
    href: (id) => `/data-hub/${id}`,
  },
  kb: { label: "Knowledge base", icon: "kb", href: (id) => `/data-hub/${id}` },
  inbox: { label: "Inbox", icon: "inbox", href: (id) => `/inbox/${id}` },
  file: { label: "File", icon: "file" },
  secret: { label: "Secret", icon: "key" },
  deployment: { label: "Deployment", icon: "app-window" },
  connection: { label: "Connection", icon: "plug" },
};

interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fileContent: string;
  fileName: string;
  isImporting: boolean;
  onConfirm: (options: ImportOptions) => void;
}

export function ImportOptionsDialog({
  isOpen,
  onOpenChange,
  fileContent,
  fileName,
  isImporting,
  onConfirm,
}: Props) {
  const bundle = useMemo(
    () => (fileContent ? decodeBundle(fileContent) : null),
    [fileContent]
  );
  const metadata = bundle?.metadata ?? [];
  // One row per entity: an all-versions export carries one artifact per saved
  // version sharing the agent's id — they'd render as duplicate rows with
  // colliding React keys.
  const artifacts = useMemo(() => {
    const seen = new Set<string>();
    return (bundle?.artifacts ?? []).filter((a) => {
      const key = `${a.type}-${a.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [bundle?.artifacts]);
  const isV3 = bundle !== null;

  // v3 bundles default to ``replace`` (override) so cross-env promotion
  // overwrites the matching artifact. Legacy bundles can't promote and are
  // pinned to clone.
  const [mode, setMode] = useState<ImportMode>(isV3 ? "replace" : "clone");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const effectiveMode: ImportMode = isV3 ? mode : "clone";

  const handleConfirm = () => {
    onConfirm({
      mode: effectiveMode,
      // Honor the exporter's activation hint by default. The receiving
      // backend applies it when the mode supports activation (replace /
      // version) and ignores it for clone.
      activate: bundle?.setActiveOnImport ?? false,
      // Scrub missing references with a warning rather than failing — the
      // common case is a stale tool reference we shouldn't block on.
      on_missing_dependency: "warn",
    });
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={(open) => !open && onOpenChange(false)}
      title="Import AI tools"
      description={`Importing ${fileName} into this workspace.`}
      size="lg"
      footerActions={[
        {
          label: "Cancel",
          props: {
            variant: "outline" as const,
            onClick: () => onOpenChange(false),
          },
        },
        {
          label: "Import",
          props: {
            onClick: handleConfirm,
            loading: isImporting,
          },
        },
      ]}
    >
      <div className="flex flex-col gap-5">
        {artifacts.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">AI tools in this bundle</span>
            <div className="flex flex-col gap-1 max-h-[260px] overflow-y-auto">
              {artifacts.map((a) => (
                <ArtifactRow key={`${a.type}-${a.id}`} artifact={a} />
              ))}
            </div>
          </div>
        )}

        {isV3 && (
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              <ChevronRight
                size={14}
                className={cn(
                  "transition-transform",
                  advancedOpen && "rotate-90"
                )}
              />
              Advanced
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col gap-4 pt-3">
              {isV3 && metadata.length > 0 && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">Bundle metadata</span>
                    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {metadata.map((m) => (
                        <Fragment key={m.label}>
                          <dt>{m.label}</dt>
                          <dd className="text-foreground break-all">
                            {m.value}
                          </dd>
                        </Fragment>
                      ))}
                    </dl>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium">Import mode</Label>
                <RadioGroup
                  value={effectiveMode}
                  onValueChange={(v) => setMode(v as ImportMode)}
                  className="flex flex-col gap-3"
                >
                  <ModeOption
                    value="clone"
                    label="Clone"
                    description="Bring the AI tools in as new copies with fresh IDs. Anything already in this workspace stays untouched."
                  />
                  <ModeOption
                    value="replace"
                    label="Override"
                    description="If an AI tool from the bundle already exists in this workspace, overwrite it in place. Otherwise create it fresh."
                    disabled={!isV3}
                  />
                </RadioGroup>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </BaseDialog>
  );
}

function ArtifactRow({ artifact }: { artifact: BundleArtifactPreview }) {
  const meta = TYPE_LABELS[artifact.type] ?? {
    label: artifact.type,
    icon: "workflow",
  };
  const icon = artifact.icon || meta.icon;
  const href = meta.href?.(artifact.id);

  const content = (
    <>
      <NoxusIcon icon={icon} size="md" />
      <div className="flex flex-col">
        <span className="text-sm">{artifact.name}</span>
        <span className="text-xs text-muted-foreground">{meta.label}</span>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="flex items-center w-full gap-3 rounded-lg p-2 hover:bg-accent transition-colors group"
      >
        {content}
        <ExternalLink
          size={16}
          className="text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
          strokeWidth={1.33}
        />
      </a>
    );
  }
  return (
    <div className="flex items-center w-full gap-3 rounded-lg p-2">
      {content}
    </div>
  );
}

function ModeOption({
  value,
  label,
  description,
  disabled,
}: {
  value: string;
  label: string;
  description: string;
  disabled?: boolean;
}) {
  return (
    <label
      htmlFor={`import-mode-${value}`}
      className={cn(
        "flex items-start gap-3 rounded-md border p-3 cursor-pointer",
        disabled ? "opacity-50 cursor-not-allowed" : "hover:border-primary/50"
      )}
    >
      <RadioGroupItem
        id={`import-mode-${value}`}
        value={value}
        disabled={disabled}
        className="mt-0.5"
      />
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </div>
    </label>
  );
}
